// The only place the QR Codes tab talks to the transparency QR API
// (transparency `src/lib/qr/api.ts`, `specs/2026-09-29-qr-codes.md` Q5 to Q7,
// served at `/v1/qr/*`). Typed to what the Worker actually returns.
//
// The portal is not a backend (I-4): nothing here recomputes a figure.

import type { Enveloped, WindowDays } from "./api";

/** The scans endpoint also allows a year (transparency spec S); nothing else does. */
export type ScanDays = 7 | 30 | 90 | 365;
export type ScanGrain = "day" | "week" | "month";

/** Where a printed code sits. A code can sit in several places (transparency spec P2). */
export const PLACEMENTS = ["label", "box", "letter", "insert", "batch_report", "event", "other"] as const;
export type QrPlacement = (typeof PLACEMENTS)[number];

export const STATUSES = ["draft", "live", "paused", "retired"] as const;
export type QrStatus = (typeof STATUSES)[number];

/** One row of `GET /v1/qr/codes`. */
export type QrCodeSummary = {
  id: string;
  /** Absolute, e.g. `https://ketosource.co/c8mct`. A family code ends in `/`. */
  printed_url: string;
  /** `<Funnel> · <Product>` (transparency spec N). */
  label: string;
  placements: QrPlacement[];
  product: string | null;
  campaign: string | null;
  print_run: string | null;
  status: QrStatus;
  target_url: string;
  scans: number;
  unique_scanners: number;
  /** Latest day with a scan inside the window, else `null`. */
  last_scan_day: string | null;
  /** First day scan counting has data for this code, else `null`. */
  counting_since: string | null;
};

export type QrCodesData = { days: number; codes: QrCodeSummary[] };

export type QrCode = Omit<QrCodeSummary, "scans" | "unique_scanners" | "last_scan_day" | "counting_since"> & {
  fallback_url: string;
  tag_destination: boolean;
  notes: string | null;
  updated_at: string;
  updated_by: string;
};

export type QrChange = {
  changed_at: string;
  changed_by: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
};

/** The draft release this code is in, if any. `preview_url` is `null` for a caller without edit. */
export type QrPendingRelease = { id: number; name: string; target_url: string; preview_url: string | null };

/** `GET /v1/qr/codes/{id}`. Days with no scans are absent from `daily`. */
export type QrCodeDetailData = {
  code: QrCode;
  days: number;
  counting_since: string | null;
  totals: { scans: number; unique_scanners: number };
  daily: { day: string; scans: number; unique_scanners: number }[];
  by_country: { country: string; scans: number }[];
  by_device: { device: string; scans: number }[];
  /** Present only for a family code (the batch report QR): top 50 by scans. */
  by_key?: { key: string; scans: number }[];
  /** Newest first. */
  changes: QrChange[];
  pending_release?: QrPendingRelease | null;
};

export type QrMe = { email: string; can_edit: boolean; can_approve: boolean };

/** Fields a PATCH may carry (the Worker rejects anything else). */
export type QrPatch = Partial<{
  label: string;
  placements: QrPlacement[];
  product: string | null;
  campaign: string | null;
  print_run: string | null;
  target_url: string;
  fallback_url: string;
  tag_destination: boolean;
  status: QrStatus;
  notes: string | null;
}>;

/** `POST /v1/qr/codes`. Always created as a draft. */
export type QrCreate = {
  id: string;
  printed_path: string;
  label: string;
  placements: QrPlacement[];
  product?: string | null;
  campaign?: string | null;
  print_run?: string | null;
  target_url: string;
  fallback_url: string;
  tag_destination?: boolean;
  notes?: string | null;
};

export type ReleaseStatus = "draft" | "published" | "rolled_back" | "discarded";

/** One row of `GET /v1/qr/releases`. */
export type QrReleaseSummary = {
  id: number;
  name: string;
  status: ReleaseStatus;
  change_count: number;
  created_at: string;
  created_by: string;
  published_at: string | null;
  published_by: string | null;
  closed_at: string | null;
};

export type QrReleaseChange = {
  code_id: string;
  label: string;
  printed_url: string;
  current_target_url: string;
  target_url: string;
  previous_target_url: string | null;
  /** `null` for a caller without edit, and once the release is no longer a draft. */
  preview_url: string | null;
};

/** `GET /v1/qr/releases/{id}`: the release and its changes. */
export type QrRelease = Omit<QrReleaseSummary, "change_count"> & {
  notes: string | null;
  changes: QrReleaseChange[];
};

export type QrReleasesData = { releases: QrReleaseSummary[] };

/** `GET /v1/qr/scans`: one row per period per code. The Worker does the week and month roll-up. */
export type QrScanRow = { period: string; code_id: string; scans: number; unique_scanners: number };
export type QrScansData = { days: number; grain: ScanGrain; rows: QrScanRow[] };

// Same-origin via the Pages Function proxy (functions/qr-codes/[[path]].ts).
const BASE = import.meta.env.VITE_QR_CODES_API_BASE ?? "/qr-codes/v1";

export class QrCodesError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`QR Codes API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function call<T>(method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE", path: string, body?: unknown): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: body === undefined ? { Accept: "application/json" } : { Accept: "application/json", "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new QrCodesError(response.status, await safeBody(response));
  return (await response.json()) as Enveloped<T>;
}

export const getCodes = (days: WindowDays) => call<QrCodesData>("GET", `/codes?days=${days}`);

export const getCode = (id: string, days: WindowDays) =>
  call<QrCodeDetailData>("GET", `/codes/${encodeURIComponent(id)}?days=${days}`);

export const getMe = () => call<QrMe>("GET", "/me");

export const createCode = (body: QrCreate) => call<{ id: string; status: "draft" }>("POST", "/codes", body);

export const patchCode = (id: string, body: QrPatch) =>
  call<{ id: string; changed: string[] }>("PATCH", `/codes/${encodeURIComponent(id)}`, body);

/** The printable SVG (a download); the proxy passes it through untouched. */
export const imageUrl = (id: string) => `${BASE}/codes/${encodeURIComponent(id)}/image.svg`;

const enc = encodeURIComponent;

export const getScans = (days: ScanDays, grain: ScanGrain, codes: string[]) =>
  call<QrScansData>("GET", `/scans?days=${days}&grain=${grain}${codes.length > 0 ? `&codes=${codes.map(enc).join(",")}` : ""}`);

export const getReleases = () => call<QrReleasesData>("GET", "/releases");
/** The Worker nests the release (`{release, changes}` on read, `{release}` on create); the tab reads it flat. */
type QrReleaseWire = { release: Omit<QrRelease, "changes">; changes?: QrReleaseChange[] };
const flattenRelease = ({ data, meta }: Enveloped<QrReleaseWire>): Enveloped<QrRelease> => ({
  data: { ...data.release, changes: data.changes ?? [] },
  meta,
});
export const getRelease = async (id: number) => flattenRelease(await call<QrReleaseWire>("GET", `/releases/${id}`));
export const createRelease = async (body: { name: string; notes?: string }) =>
  flattenRelease(await call<QrReleaseWire>("POST", "/releases", body));
export const patchRelease = (id: number, body: { name?: string; notes?: string }) => call<QrRelease>("PATCH", `/releases/${id}`, body);
export const putReleaseChange = (id: number, codeId: string, target_url: string) =>
  call<unknown>("PUT", `/releases/${id}/changes/${enc(codeId)}`, { target_url });
export const deleteReleaseChange = (id: number, codeId: string) => call<unknown>("DELETE", `/releases/${id}/changes/${enc(codeId)}`);
// An empty JSON body on purpose: the Worker refuses a cross-site POST with no Content-Type (src/lib/site/origin-check.ts).
export const publishRelease = (id: number) => call<unknown>("POST", `/releases/${id}/publish`, {});
export const rollbackRelease = (id: number) => call<unknown>("POST", `/releases/${id}/rollback`, {});
export const discardRelease = (id: number) => call<unknown>("POST", `/releases/${id}/discard`, {});

/** The test link's QR image, so a phone can scan it (draft releases only, edit users only). */
export const releasePreviewSvgUrl = (id: number, codeId: string) => `${BASE}/releases/${id}/changes/${enc(codeId)}/preview.svg`;

/** One query key per read. Every key starts `qr_codes` so one invalidate refreshes them all. */
export const qrCodesKey = (days: WindowDays) => ["qr_codes", "codes", days] as const;
export const qrCodeKey = (id: string, days: WindowDays) => ["qr_codes", "code", id, days] as const;
export const qrMeKey = ["qr_codes", "me"] as const;
export const qrScansKey = (days: ScanDays, grain: ScanGrain, codes: string[]) => ["qr_codes", "scans", days, grain, codes.join(",")] as const;
export const qrReleasesKey = ["qr_codes", "releases"] as const;
export const qrReleaseKey = (id: number) => ["qr_codes", "release", id] as const;

function bodyField(error: unknown, field: "error" | "detail" | "field"): string {
  return error instanceof QrCodesError && error.body !== null && typeof error.body === "object"
    ? String((error.body as Record<string, unknown>)[field] ?? "")
    : "";
}

/** The codes a `409 changed_since` names, from whichever list field the Worker sent. */
function namedCodes(error: unknown): string {
  if (!(error instanceof QrCodesError) || error.body === null || typeof error.body !== "object") return "";
  const body = error.body as Record<string, unknown>;
  const list = body.codes ?? body.changed ?? body.detail;
  if (Array.isArray(list)) {
    return list
      .map((item) => {
        if (item !== null && typeof item === "object") {
          const row = item as Record<string, unknown>;
          return String(row.label ?? row.code_id ?? "");
        }
        return String(item);
      })
      .filter((text) => text !== "")
      .join(", ");
  }
  return typeof list === "string" ? list : "";
}

/** One sentence per refused write, in the marketer's words. */
export function qrWriteMessage(error: unknown): string {
  const code = bodyField(error, "error");
  if (code === "changed_since") {
    const named = namedCodes(error);
    return `These codes changed after this release, so it cannot be rolled back${named ? `: ${named}` : ""}.`;
  }
  if (code === "in_other_release") {
    // The Worker names the other release as `release: {id, name}`.
    const other = error instanceof QrCodesError ? (error.body as { release?: { name?: unknown } } | null)?.release : undefined;
    const named = typeof other?.name === "string" ? other.name : bodyField(error, "detail");
    return `That code is already in another draft release${named ? `: ${named}` : ""}. Remove it there first.`;
  }
  const messages: Record<string, string> = {
    approver_required: "Only an approver can change where a live code goes.",
    conflict: "A code with that short id or address already exists. Pick another short id.",
    not_found: "That code could not be found. Reload and try again.",
    not_draft: "This release is no longer a draft, so it cannot be changed. Reload to see its state.",
    qr_unavailable: "The change could not be saved because the QR service did not answer. Nothing was changed. Try again shortly.",
  };
  if (messages[code]) return messages[code];
  // A 403 without approver_required (release create, edit, publish, roll back) is a missing grant.
  if (error instanceof QrCodesError && error.status === 403) {
    return "You do not have permission to make this change. Ask an approver.";
  }
  if (code === "invalid") {
    const detail = bodyField(error, "detail");
    return `That value is not accepted${detail ? `: ${detail}` : ""}. Nothing was changed.`;
  }
  const cause = error instanceof QrCodesError ? `HTTP ${error.status}` : "no response";
  return `The change could not be saved. Nothing was changed. Try again shortly. (${cause})`;
}

/** One message per read failure. */
export function qrReadMessage(error: unknown, areaName: string): string {
  if (error instanceof QrCodesError && error.status === 403) {
    return `You do not have permission to see this. Ask the ${areaName} area owner.`;
  }
  if (error instanceof QrCodesError && error.status === 401) {
    return "Your session could not be confirmed. Reload the page to sign in again.";
  }
  if (error instanceof QrCodesError && error.status === 404) return "That code could not be found.";
  return "QR stats are unavailable. Printed codes still work.";
}
